import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, FormGroup, FormControlLabel } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import { withErrorBoundary } from 'react-error-boundary';
import { find, isEmpty } from 'lodash';
import { Button, Typography } from 'cprime-foundational-components';
import AlertMessage from 'components/AlertMessage/AlertMessage';
import {
  CascadingFiltersHeader,
  CascadingFiltersListSection,
  CascadingFiltersSection,
} from 'components/CascadingFilters/components';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import * as S from './CascadingFilters.styles';

const CascadingFilters = ({
  applyFiltersHandler,
  businessSalesMonthData,
  cascadingFiltersConfig,
  cascadingFiltersLocal,
  cascadingOptions,
  defaultFilters = [],
  getChipLabel,
  getListOptions,
  hasUncheckedSection,
  isCascadingFilterOff,
  setCascadingFiltersLocal,
  setCascadingOptions,
  setIsCascadingFilterOff,
}) => {
  const [openSection, setOpenSection] = useState();
  const [listSectionData, setListSectionData] = useState(null);

  const resetAllHandler = () => {
    setCascadingFiltersLocal(defaultFilters ? defaultFilters : []);
    setOpenSection();
  };
  const sideFiltersComponent = {
    CascadingFiltersSection,
    CascadingFiltersHeader,
  };
  const getComponentByType = (name) => sideFiltersComponent[name] || null;

  const sectionParameters = {
    setCascadingFilters: setCascadingOptions,
    cascadingFilters: cascadingOptions,
    setCascadingFiltersLocal: setCascadingFiltersLocal,
    cascadingFiltersLocal: cascadingFiltersLocal,
    setListSectionData: setListSectionData,
    cascadingFiltersConfig: cascadingFiltersConfig,
    isCascadingFilterOff: isCascadingFilterOff,
    businessSalesMonthData,
  };

  return (
    <S.StyledBox sx={{ width: { mobile: '12rem', tablet: '16rem' }, paddingBottom: '1rem' }}>
      {listSectionData ? (
        <CascadingFiltersListSection
          headerName={listSectionData.headerName}
          options={getListOptions({ headerName: listSectionData.headerName })}
          applyFiltersHandler={applyFiltersHandler}
          resetAllHandler={resetAllHandler}
          hasUncheckedSection={hasUncheckedSection}
          {...sectionParameters}
        />
      ) : (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            p="1rem"
            bgcolor="primary.main"
          >
            <Typography variant="body1" color="text.white">
              Global Filters
            </Typography>
            <PublicIcon fontSize="small" sx={{ color: 'text.white' }} />
          </Stack>
          {isEmpty(cascadingOptions) ? (
            <LoadingSpinner fullScreen />
          ) : (
            <>
              <Stack
                p="1rem"
                direction="row"
                sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
              >
                <FormGroup>
                  <FormControlLabel
                    variant="body2"
                    sx={{ height: '1rem' }}
                    control={
                      <S.StyledFilterSwitch
                        checked={!isCascadingFilterOff}
                        onClick={() => setIsCascadingFilterOff(!isCascadingFilterOff)}
                      />
                    }
                    label={<Typography variant="body1">Cascading Filters</Typography>}
                  />
                </FormGroup>
              </Stack>
              <Box
                sx={{
                  maxHeight: 'calc(100% - 10rem)',
                  overflow: 'auto',
                  overflowX: 'clip',
                }}
              >
                {cascadingOptions.map((cascadingFilterObj) => {
                  const cascadingFilter = find(cascadingOptions, {
                    field: cascadingFilterObj.field,
                  });
                  const SideFilterSection = getComponentByType(cascadingFilterObj.type);
                  return (
                    SideFilterSection && (
                      <SideFilterSection
                        isDisabled={cascadingFilter.isDisabled}
                        disabledTooltip={cascadingFilter.disabledTooltip}
                        openSection={openSection}
                        setOpenSection={setOpenSection}
                        options={cascadingFilter.options}
                        key={cascadingFilterObj.field}
                        {...cascadingFilterObj}
                        {...sectionParameters}
                        getChipLabel={getChipLabel}
                      />
                    )
                  );
                })}
              </Box>
              <Stack p="1rem" pb={0} spacing={1} direction="row">
                <Button variant="tertiary" onClick={() => resetAllHandler()}>
                  Reset All
                </Button>
                <Button
                  variant="primary"
                  onClick={() => applyFiltersHandler({})}
                  disabled={hasUncheckedSection()}
                >
                  Apply Filters
                </Button>
              </Stack>
            </>
          )}
        </>
      )}
    </S.StyledBox>
  );
};

CascadingFilters.propTypes = {
  applyFiltersHandler: PropTypes.func,
  businessSalesMonthData: PropTypes.object,
  cascadingFiltersConfig: PropTypes.array,
  cascadingFiltersLocal: PropTypes.array,
  cascadingOptions: PropTypes.array,
  defaultFilters: PropTypes.array,
  getCascadingFiltersInitial: PropTypes.func,
  getChipLabel: PropTypes.func,
  getListOptions: PropTypes.func,
  hasUncheckedSection: PropTypes.func,
  isCascadingFilterOff: PropTypes.bool,
  setCascadingFiltersLocal: PropTypes.func,
  setCascadingOptions: PropTypes.func,
  setIsCascadingFilterOff: PropTypes.func,
};

export default withErrorBoundary(CascadingFilters, {
  fallback: <AlertMessage />,
});
